/* Common Imports */

import { styled, useMediaQuery, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import { fetchRealtimeNewChatMessageThunk, setOpenChatModalThunk } from "../../../redux-magic/store";

/* Component Imports */

import { Badge, SwipeableDrawer, Typography } from "@mui/material";
import ChatListScreen from "./ChatListScreen/ChatListScreen";
import SingleChatScreen from "./SingleChatScreen/SingleChatScreen";

/* Icon Imports */

import { ExpandLessOutlined, MessageOutlined } from "@mui/icons-material";

/* Styled Components */

const MessageButtonMobile = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: "8px",
	background: theme.palette.primary.main,
	color: theme.palette.primary.contrastText,
	padding: "0.5rem 1rem",
	borderRadius: "12px",
	position: "fixed",
	bottom: theme.spacing(9.5),
	right: theme.spacing(2),
	zIndex: 5,
	cursor: "pointer",
	boxShadow: "0px 17px 10px -10px rgba(0,0,0,0.4)",
	transition: "all ease-in-out 300ms",
	"&:hover": {
		background: "#feeada",
		boxShadow: "0px 37px 20px -15px rgba(0,0,0,0.2)",
		transform: "translate(0px, -10px)",
	},
}));

const MessageButton = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "8px",
	background: theme.palette.background.paper,
	padding: "1rem 1rem 1rem 1rem",
	borderRadius: "1.5rem 1.5rem 0rem 0rem",
	borderTop: "2px solid #e0e0e0",
	borderLeft: "2px solid #e0e0e0",
	borderRight: "2px solid #e0e0e0",
	position: "fixed",
	bottom: 0,
	right: 16,
	zIndex: 5,
	cursor: "pointer",
	transition: "all ease-in-out 200ms",
	width: "22rem",
	"&:hover": {
		boxShadow: "-4px -4px 8px -4px rgba(0,0,0,0.1), 4px -4px 8px -4px rgba(0,0,0,0.1)",
	},
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const ProfilePicture = styled("img")(({ theme }) => ({
	borderRadius: "0.5rem",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "2.5rem",
	width: "2.5rem",
	[theme.breakpoints.up("md1190")]: {
		height: "2.5rem",
		width: "2.5rem",
	},
}));

const UnreadChip = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	background: theme.palette.background.paper,
	borderRadius: "1.5rem",
	padding: "0.25rem 0.75rem",
}));

const DrawerContainer = styled(SwipeableDrawer)(({ theme }) => ({
	"& .MuiDrawer-paper": {
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-end",
		alignItems: "flex-end",
		overflowY: "hidden",
		background: theme.palette.background.paper,
		borderRadius: "0rem 0rem 0rem 0rem",
		padding: "0rem 0rem 0rem 0rem",
		width: "100%",
		height: "100vh",
		marginTop: "0px",
		right: "auto",
		left: "auto",
		borderTop: "2px solid #e0e0e0",
		borderLeft: "2px solid #e0e0e0",
		borderRight: "2px solid #e0e0e0",
		position: "fixed",
		[theme.breakpoints.up("md1190")]: {
			width: "28rem",
			// height: "calc(100% - 70px)",
			boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
			height: "80%",
			marginTop: "70px",
			borderRadius: "1.5rem 1.5rem 0rem 0rem",
			right: 16,
			padding: "0rem 0rem 0rem 0rem",
		},
	},
	"& .MuiBackdrop-root": {
		backgroundColor: "rgba(0, 0, 0, 0)",
		pointerEvents: "auto",
	},
}));

const ChatFab = ({
	session,
	profile_context,
	dispatch,
	router,
	chat_states,
	new_chat_message,
}: {
	session: StateInterface["redux_session"];
	profile_context: StateInterface["profile_context"];
	dispatch: Function;
	router: NextRouter;
	chat_states: StateInterface["chat_states"];
	new_chat_message: StateInterface["chat_message"] | null;
}) => {
	const theme = useTheme();

	const isDesktop = useMediaQuery(theme.breakpoints.up("md1190"));

	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	/* Translations */

	const localLang = {
		chat: router.locale === "ar-AE" ? "دردشة" : router.locale === "kn-IN" ? "ಚಾಟ್" : "Chat",
		unread: router.locale === "ar-AE" ? "غير مقروء" : router.locale === "kn-IN" ? "ಅಪ прочитано" : "Unread",
		messages: router.locale === "ar-AE" ? "الرسائل" : router.locale === "kn-IN" ? "ಸಂದೇಶಗಳು" : "Messages",
	};

	/* Profile Picture */

	const [businessProfilePicture, setBusinessProfilePicture] = React.useState(
		profile_context
			? profile_context.business_profile_image
				? profile_context.business_profile_image.exists
					? (profile_context.business_profile_image.file_id as string)
					: "/images/icons/Avatar.svg"
				: "/images/icons/Avatar.svg"
			: "/images/icons/Avatar.svg",
	);

	React.useEffect(() => {
		setBusinessProfilePicture(
			profile_context
				? profile_context.business_profile_image
					? profile_context.business_profile_image.exists
						? (profile_context.business_profile_image.file_id as string)
						: "/images/icons/Avatar.svg"
					: "/images/icons/Avatar.svg"
				: "/images/icons/Avatar.svg",
		);
	}, [profile_context]);

	const userProfilePicture = session
		? session.user
			? session.user.dbUser.profile_picture
				? (session.user.dbUser.profile_picture.exists as boolean)
					? (session.user.dbUser.profile_picture.file_id as string)
					: "/images/icons/Avatar.svg"
				: "/images/icons/Avatar.svg"
			: "/images/icons/Avatar.svg"
		: "/images/icons/Avatar.svg";

	React.useEffect(() => {
		session
			? session.user
				? session.user.dbUser
					? session.user.dbUser.profile_picture
						? (session.user.dbUser.profile_picture.exists as boolean)
							? (session.user.dbUser.profile_picture.file_id as string)
							: "/images/icons/Avatar.svg"
						: "/images/icons/Avatar.svg"
					: "/images/icons/Avatar.svg"
				: "/images/icons/Avatar.svg"
			: "/images/icons/Avatar.svg";
	}, [session]);

	const profilePicture = profile_context
		? profile_context.is_business_profile
			? businessProfilePicture
			: userProfilePicture
		: userProfilePicture;

	/* Fetching Realtime New Chat Message */

	React.useEffect(() => {
		// const messageExists = chat_states.current_chat.messages?.filter((item: any) => item._id === new_chat_message?._id);

		if (
			new_chat_message !== null &&
			// messageExists?.length === 0 &&
			new_chat_message.receivers.some(
				(receiver: any) =>
					receiver.is_business_profile === profile_context?.is_business_profile &&
					(receiver.is_business_profile
						? receiver.business_profile_id === profile_context?.business_profile_id
						: receiver.user_id === profile_context?.user_id),
			)
		) {
			dispatch(
				fetchRealtimeNewChatMessageThunk({
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
					chat_id: new_chat_message.chat_id,
					message_id: new_chat_message._id,
					current_screen: chat_states.screen,
					is_business_profile: profile_context?.is_business_profile || false,
					user_id: profile_context?.user_id || "",
					business_profile_id: profile_context?.business_profile_id || "",
				}),
			);
		}

		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [new_chat_message]);

	// Add scroll lock management
	React.useEffect(() => {
		if (chat_states.open_chat_modal && isMobile) {
			document.body.style.overflow = "hidden";
			document.body.style.position = "fixed";
			document.body.style.width = "100%";
		} else {
			document.body.style.overflow = "";
			document.body.style.position = "";
			document.body.style.width = "";
		}

		return () => {
			document.body.style.overflow = "";
			document.body.style.position = "";
			document.body.style.width = "";
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat_states.open_chat_modal]);

	// const handleMouseEnter = () => {
	// 	document.body.style.overflow = "hidden";
	// };

	// const handleMouseLeave = () => {
	// 	document.body.style.overflow = "auto";
	// };

	const [isHovered, setIsHovered] = React.useState(false);
	const timeoutRef = React.useRef<NodeJS.Timeout>();

	const handleMouseEnter = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		if (!isHovered) {
			setIsHovered(true);
			document.body.style.overflow = "hidden";
		}
	};

	const handleMouseLeave = () => {
		timeoutRef.current = setTimeout(() => {
			setIsHovered(false);
			document.body.style.overflow = "auto";
		}, 150); // Adding small delay to prevent rapid toggling
	};

	// Cleanup timeout on unmount
	React.useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			document.body.style.overflow = "auto";
		};
	}, []);

	return (
		<React.Fragment>
			{/* Chat Fab */}

			{isDesktop ? (
				<MessageButton
					onClick={() => dispatch(setOpenChatModalThunk(true))}
					sx={{
						background:
							chat_states.chat_list.total_unread_count > 0
								? theme.palette.primary.main
								: theme.palette.background.paper,
						borderTop: chat_states.chat_list.total_unread_count > 0 ? "none" : "2px solid #e0e0e0",
						borderLeft: chat_states.chat_list.total_unread_count > 0 ? "none" : "2px solid #e0e0e0",
						borderRight: chat_states.chat_list.total_unread_count > 0 ? "none" : "2px solid #e0e0e0",
					}}
				>
					<RowContainer
						sx={{
							gap: "1rem",
						}}
					>
						{/* Profile Picture */}

						{!profilePicture.includes("/images/icons/Avatar.svg") ? (
							<ProfilePicture
								src={`/profile-picture/${profilePicture}`}
								alt={localLang.messages}
								referrerPolicy="no-referrer"
							/>
						) : (
							<ProfilePicture
								src={profilePicture}
								alt={localLang.messages}
								referrerPolicy="no-referrer"
							/>
						)}

						<Typography
							variant="body1"
							sx={{
								fontSize: "1.125rem",
								fontWeight: 500,
								lineHeight: "1.25rem",
								color:
									chat_states.chat_list.total_unread_count > 0
										? theme.palette.primary.contrastText
										: theme.palette.text.primary,
							}}
						>
							{localLang.messages}
						</Typography>

						{chat_states.chat_list.total_unread_count > 0 ? (
							<UnreadChip>
								<Typography
									variant="body2"
									sx={{
										color: theme.palette.primary.main,
										fontWeight: 600,
										lineHeight: "1.25rem",
									}}
								>
									{chat_states.chat_list.total_unread_count + " " + localLang.unread}
								</Typography>
							</UnreadChip>
						) : null}
					</RowContainer>

					<ExpandLessOutlined
						sx={{
							color:
								chat_states.chat_list.total_unread_count > 0
									? theme.palette.primary.contrastText
									: theme.palette.text.disabled,
							fontSize: "2rem",
						}}
					/>
				</MessageButton>
			) : (
				<MessageButtonMobile
					onClick={() => dispatch(setOpenChatModalThunk(true))}
					sx={{
						background: chat_states.chat_list.total_unread_count > 0 ? theme.palette.primary.main : "#feeada",
						color: chat_states.chat_list.total_unread_count > 0 ? theme.palette.primary.contrastText : "#6E3037",
					}}
				>
					<Badge
						badgeContent={
							chat_states.chat_list.total_unread_count > 9 ? "9+" : chat_states.chat_list.total_unread_count
						}
						sx={{
							"& .MuiBadge-badge": {
								background: "#f44336",
								color: theme.palette.primary.contrastText,
								top: -8,
								right: -10,
								border: `2px solid ${theme.palette.background.paper}`,
								height: "1.5rem",
								width: chat_states.chat_list.total_unread_count > 9 ? "2rem" : "1.5rem",
								fontSize: "0.875rem",
								fontWeight: 600,
								borderRadius: chat_states.chat_list.total_unread_count > 9 ? "1rem" : "50%",
							},
						}}
					>
						<RowContainer
							sx={{
								gap: "0.25rem",
							}}
						>
							<MessageOutlined
								sx={{
									fontSize: "1.25rem",
								}}
							/>
							<Typography
								variant="body1"
								sx={{
									fontSize: "0.875rem",
									fontWeight: 500,
									lineHeight: "1rem",
								}}
							>
								{localLang.chat}
							</Typography>
						</RowContainer>
					</Badge>
				</MessageButtonMobile>
			)}

			{/* Chat Drawer */}

			<DrawerContainer
				variant="persistent"
				anchor="bottom"
				open={chat_states.open_chat_modal}
				onClose={() => dispatch(setOpenChatModalThunk(false))}
				onOpen={() => dispatch(setOpenChatModalThunk(true))}
				ModalProps={{
					keepMounted: true,
				}}
				hideBackdrop={true}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{chat_states && chat_states.screen === "chat_list" ? (
					<ChatListScreen
						session={session}
						dispatch={dispatch}
						router={router}
						profilePicture={profilePicture}
						profile_context={session ? session.user.dbUser.profile_context : null}
						chat_list={chat_states.chat_list}
						broadcast_list={chat_states.broadcast_list}
						list_tab={chat_states.chat_list_tab}
					/>
				) : chat_states && chat_states.screen === "chat" ? (
					<SingleChatScreen
						session={session}
						dispatch={dispatch}
						router={router}
						profile_context={session ? session.user.dbUser.profile_context : null}
						current_chat_data={chat_states.current_chat}
						desktop_fullscreen_mode={chat_states.desktop_fullscreen_mode}
					/>
				) : null}
			</DrawerContainer>
		</React.Fragment>
	);
};

export default ChatFab;
